<script setup>
import {onMounted, ref, computed} from 'vue';
import CommonMenuBar from "@/Components/account/CommonMenuBar.vue";
import DynamicDialog from "primevue/dynamicdialog";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import Select from "primevue/select";
import {usePage, router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {useI18n} from 'vue-i18n';
import CreateSessionButton from "@/Components/admin/CreateSessionButton.vue";
import MobileTeamSelector from "@/Components/account/MobileTeamSelector.vue";
import AdminMenu from "@/Components/admin/AdminMenu.vue";

const createSessionButtonRef = ref(null);

const {t} = useI18n();

const props = defineProps({
    title: String,
    product: Object
});

const selectedTeam = ref(null);
const page = usePage();
const teams = page.props.teams;
const currentTeam = page.props.team;

const changeTeam = (event) => {
    const currentUrl = new URL(route('admin.dashboard'));
    currentUrl.searchParams.set('change-team', event.value);
    window.location.href = currentUrl.toString();
}

// Desktop menu items - empty for admin (we use AdminMenu sidebar instead)
const menuItems = computed(() => []);

// Mobile-only items that appear in the hamburger menu
const mobileMenuItems = computed(() => {
    if (!currentTeam) return [];

    const items = [];

    // Add main menu items (only visible in mobile hamburger menu)
    const mainMenuItems = [
        {
            label: t('admin.menu.dashboard'),
            command: () => window.location.href = route('admin.dashboard'),
        },
        {
            label: t('admin.menu.sessions'),
            command: () => window.location.href = route('admin.team.sessions.index', {team: currentTeam.id}),
        },
        {
            label: t('admin.menu.products'),
            command: () => window.location.href = route('admin.products.index'),
        },
        // Library (superadmin only) - appears in mobile hamburger as well
        ...(page.props.isSuperAdmin ? [{
            label: t('admin.menu.library'),
            command: () => window.location.href = route('admin.library.index'),
        }] : []),
        {
            label: t('admin.menu.icons'),
            command: () => window.location.href = route('admin.vector-images.index'),
        },
        {
            label: t('admin.menu.teams'),
            command: () => window.location.href = route('admin.teams.index'),
            visible: !!page.props.permissions.canManageTeams
        },
        {
            label: t('admin.menu.team'),
            command: () => window.location.href = route('admin.teams.show', {teamId: currentTeam.id}),
            visible: !page.props.permissions.canManageTeams
        },
        {separator: true},
        {
            label: t('admin.menu.account'),
            disabled: true,
            class: 'admin-menu-mobile-section-label',
        },
        {
            label: t('admin.menu.settings'),
            icon: 'pi pi-cog',
            command: () => window.location.href = route('admin.user.show'),
        },
        {
            label: t('admin.menu.logout'),
            icon: 'pi pi-sign-out',
            command: () => router.post(route('admin.perform-logout')),
        },
    ].filter(item => item.visible !== false);

    items.push(...mainMenuItems);

    // Add create session link
    items.push({
        label: t('admin.menu.create_session'),
        icon: 'pi pi-plus',
        command: () => {
            if (createSessionButtonRef.value) {
                createSessionButtonRef.value.openCreateDialog();
            }
        }
    });

    // Add team selector if multiple teams
    if (teams.length > 1) {
        items.push({
            component: MobileTeamSelector,
            label: '' // Empty label as the component handles its own rendering
        });
    }

    return items;
});

onMounted(() => {
    document.documentElement.classList.remove('configurator-dark');

    if (teams.length > 1) {
        selectedTeam.value = page.props.team.id;

        if (window.bugsnagClient) {
            // set team
            window.bugsnagClient.addMetadata('team', {
                id: page.props.team.id,
                name: page.props.team.name,
            });

            // set user
            window.bugsnagClient.addMetadata('user', {
                id: page.props.user.id,
                name: page.props.user.name,
            });
        }
    }
})

</script>

<template>
    <div class="w-auto admin-layout">
        <Toast/>
        <ConfirmDialog/>
        <CommonMenuBar :logo-url="'admin.dashboard'" :items="menuItems" :mobile-items="mobileMenuItems">
            <template #end>
                <div class="flex gap-2 align-items-center w-full">
                    <CreateSessionButton class="hidden md:flex" ref="createSessionButtonRef" v-if="$page.props.auth?.user"/>
                    <Select @change="changeTeam" v-if="teams.length > 1" class="w-10"
                            :placeholder="t('admin.teams.select_team')" large optionLabel="name"
                            option-label="name" option-value="id" :options="teams" v-model="selectedTeam"></Select>
                </div>
            </template>
        </CommonMenuBar>
        <div class="grid">
            <div v-if="$page.props.auth?.user" class="hidden md:block md:col-2 md:mt-2">
                <AdminMenu></AdminMenu>
            </div>
            <DynamicDialog/>
            <div v-if="$page.props.auth?.user" class="col-12 md:col-10 md:mt-2">
                <main>
                    <slot/>
                </main>
            </div>
            <div v-else class="col-12 md:col-offset-4 md:col-4 md:mt-2 md:p-5">
                <main>
                    <slot/>
                </main>
            </div>

        </div>
    </div>
</template>

<style scoped lang="scss">
// Ensure no horizontal overflow
.w-auto {
    overflow-x: hidden;
}

// Make cards and containers responsive
@media screen and (max-width: 768px) {
    :deep(.card) {
        margin: 0 !important;
        border-radius: 0 !important;
    }

    :deep(.p-3) {
        padding: 1rem !important;
    }

    // Reduce grid gaps on mobile
    .grid {
        margin: 0 !important;
    }

    .col-12 {
        padding: 0.5rem !important;
    }
}
</style>
